'use client';

import { useCallback } from 'react';
import { useTranslations } from 'next-intl';
import { SchemaOrgItemProp, SchemaOrgItemType } from '@headless-workspace/config';
import { GlowProductRating, PropsWithFontFamily } from '@headless-workspace/glow-ds';
import { Component } from '@headless-workspace/typings';

type ProductRatingProps = PropsWithFontFamily & {
    rating: number;
    reviewCount: number;
    productName?: string;
};

export const PRODUCT_DETAIL_RATING_SECTION_ID = 'product-detail-ratings-and-reviews-section';

export const ProductRating: Component<ProductRatingProps> = ({ rating, reviewCount, productName, fontFamily }) => {
    const t = useTranslations('Discover.Pdp.information.rating');

    const onRatingClick = useCallback(() => {
        if (typeof document !== 'undefined') {
            const element = document.getElementById(PRODUCT_DETAIL_RATING_SECTION_ID);
            element?.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
            });
        }
    }, []);

    if (reviewCount <= 0) {
        return (
            <GlowProductRating
                rating={rating}
                reviewCount={reviewCount}
                fontFamily={fontFamily}
                ariaLabel={t('ariaLabel.reviews')}
                reviewsText={t('action.reviews')}
                noReviewsText={t('label.noReview')}
                onClick={onRatingClick}
            />
        );
    }

    return (
        <div itemProp={SchemaOrgItemProp.AggregateRating} itemScope itemType={SchemaOrgItemType.AggregateRating}>
            <GlowProductRating
                rating={rating}
                reviewCount={reviewCount}
                fontFamily={fontFamily}
                ariaLabel={t('ariaLabel.reviews')}
                reviewsText={t('action.reviews')}
                noReviewsText={t('label.noReview')}
                onClick={onRatingClick}
                structuredData={{
                    ratingItemProp: SchemaOrgItemProp.RatingValue,
                    reviewItemProp: SchemaOrgItemProp.ReviewCount,
                }}
            />
            {productName && (
                <span
                    itemProp={SchemaOrgItemProp.ItemReviewed}
                    itemScope
                    itemType={SchemaOrgItemType.IndividualProduct}
                    className={'hidden'}
                    aria-hidden
                >
                    <span itemProp={SchemaOrgItemProp.Name}>{productName}</span>
                </span>
            )}
        </div>
    );
};
